import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { MoreHorizontal, Printer, FileText, X } from "lucide-react";
import generarReciboPdf from "./reciboPdfGenerator";

const API = process.env.REACT_APP_BACKEND_URL;

const ComprobantesOficina = ({ oficina, endpointParam, emptyMessage, testIdPrefix }) => {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState([]);
  const [columnFilters, setColumnFilters] = useState({
    nro_recibo: "", rif: "", nombre: "", fecha: "", tramite: "", banco: "", oficina: "", monto: "", estatus: ""
  });
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfFileName, setPdfFileName] = useState("");

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/api/recaudaciones/comprobantes-pago?${endpointParam}`, { headers });
      setData(response.data);
    } catch { toast.error("Error al cargar datos"); }
  };

  useEffect(() => { fetchData(); }, []);

  const handlePrintRecibo = async (comprobante) => {
    try {
      toast.info("Generando recibo...");
      const { pdfUrl, pdfFileName: name, pdfDoc } = await generarReciboPdf(comprobante, headers);
      setPdfPreviewUrl(pdfUrl);
      setPdfFileName(name);
      setPdfPreviewOpen(true);
      window._currentPdfDoc = pdfDoc;
    } catch (error) {
      toast.error(`Error al generar el recibo: ${error.message || "Error desconocido"}`);
    }
  };

  const handleDownloadPdf = () => {
    if (window._currentPdfDoc && pdfFileName) {
      window._currentPdfDoc.save(pdfFileName);
      toast.success("Recibo descargado exitosamente");
    }
  };

  const handleClosePdfPreview = () => {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewOpen(false);
    setPdfPreviewUrl(null);
    setPdfFileName("");
    window._currentPdfDoc = null;
  };

  const filteredData = data.filter(item => {
    const cf = columnFilters;
    if (cf.nro_recibo && !(item.nro_recibo || "").toLowerCase().includes(cf.nro_recibo.toLowerCase())) return false;
    if (cf.rif && !(item.rif || "").toLowerCase().includes(cf.rif.toLowerCase())) return false;
    if (cf.nombre && !(item.nombre_razon_social || "").toLowerCase().includes(cf.nombre.toLowerCase())) return false;
    if (cf.fecha && !(item.fecha_recaudacion || "").includes(cf.fecha)) return false;
    if (cf.tramite && !(item.tramite_por_pagar_nro || "").toLowerCase().includes(cf.tramite.toLowerCase())) return false;
    if (cf.banco && !(item.banco_emisor_nombre || "").toLowerCase().includes(cf.banco.toLowerCase())) return false;
    if (cf.oficina && !(item.oficina || "").toLowerCase().includes(cf.oficina.toLowerCase())) return false;
    if (cf.monto && !String(item.monto || "").includes(cf.monto)) return false;
    if (cf.estatus && cf.estatus !== "todos" && item.estatus !== cf.estatus) return false;
    return true;
  }).sort((a, b) => (b.nro_recibo || "").localeCompare(a.nro_recibo || "", undefined, { numeric: true }));

  const pfx = testIdPrefix;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="px-3 py-3 text-left text-sm font-medium text-slate-600">Nro. Recibo</th>
                  <th className="px-3 py-3 text-left text-sm font-medium text-slate-600">RIF</th>
                  <th className="px-3 py-3 text-left text-sm font-medium text-slate-600">Nombre / Razón Social</th>
                  <th className="px-3 py-3 text-left text-sm font-medium text-slate-600">Fecha</th>
                  <th className="px-3 py-3 text-left text-sm font-medium text-slate-600">Trámite</th>
                  <th className="px-3 py-3 text-left text-sm font-medium text-slate-600">Banco</th>
                  <th className="px-3 py-3 text-left text-sm font-medium text-slate-600">Oficina</th>
                  <th className="px-3 py-3 text-right text-sm font-medium text-slate-600">Monto</th>
                  <th className="px-3 py-3 text-left text-sm font-medium text-slate-600">Estatus</th>
                  <th className="px-3 py-3 text-center text-sm font-medium text-slate-600">Acciones</th>
                </tr>
                <tr className="border-b bg-white">
                  <th className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={columnFilters.nro_recibo} onChange={(e) => setColumnFilters({...columnFilters, nro_recibo: e.target.value})} data-testid={`filter-${pfx}-nro-recibo`} /></th>
                  <th className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={columnFilters.rif} onChange={(e) => setColumnFilters({...columnFilters, rif: e.target.value})} data-testid={`filter-${pfx}-rif`} /></th>
                  <th className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={columnFilters.nombre} onChange={(e) => setColumnFilters({...columnFilters, nombre: e.target.value})} data-testid={`filter-${pfx}-nombre`} /></th>
                  <th className="px-3 py-1.5"><Input type="date" className="h-7 text-xs" value={columnFilters.fecha} onChange={(e) => setColumnFilters({...columnFilters, fecha: e.target.value})} data-testid={`filter-${pfx}-fecha`} /></th>
                  <th className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={columnFilters.tramite} onChange={(e) => setColumnFilters({...columnFilters, tramite: e.target.value})} data-testid={`filter-${pfx}-tramite`} /></th>
                  <th className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={columnFilters.banco} onChange={(e) => setColumnFilters({...columnFilters, banco: e.target.value})} data-testid={`filter-${pfx}-banco`} /></th>
                  <th className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={columnFilters.oficina} onChange={(e) => setColumnFilters({...columnFilters, oficina: e.target.value})} data-testid={`filter-${pfx}-oficina`} /></th>
                  <th className="px-3 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={columnFilters.monto} onChange={(e) => setColumnFilters({...columnFilters, monto: e.target.value})} data-testid={`filter-${pfx}-monto`} /></th>
                  <th className="px-3 py-1.5">
                    <select className="h-7 w-full text-xs border rounded-md px-1 bg-white text-slate-700" value={columnFilters.estatus} onChange={(e) => setColumnFilters({...columnFilters, estatus: e.target.value})} data-testid={`filter-${pfx}-estatus`}>
                      <option value="">Todos</option><option value="Pagado">Pagado</option><option value="Pendiente">Pendiente</option><option value="Anulado">Anulado</option>
                    </select>
                  </th>
                  <th className="px-3 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr><td colSpan="10" className="px-4 py-8 text-center text-slate-500">{emptyMessage}</td></tr>
                ) : (
                  filteredData.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{item.nro_recibo}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.rif}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.nombre_razon_social}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.fecha_recaudacion}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.tramite_por_pagar_nro}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.banco_emisor_nombre}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.oficina || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-900 text-right font-medium">{item.monto?.toLocaleString('es-VE', { minimumFractionDigits: 2 })}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{item.estatus}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`row-actions-${pfx}-${item.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handlePrintRecibo(item)} data-testid={`btn-imprimir-${pfx}-${item.id}`}>
                              <Printer className="w-4 h-4 mr-2" />Imprimir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Modal de Previsualización de PDF */}
      <Dialog open={pdfPreviewOpen} onOpenChange={handleClosePdfPreview}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col" data-testid="pdf-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" />Previsualización del Recibo</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-gray-100 rounded-lg overflow-hidden">
            {pdfPreviewUrl && <iframe src={pdfPreviewUrl} className="w-full h-full border-0" title="Previsualización PDF" data-testid="pdf-preview-iframe" />}
          </div>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={handleClosePdfPreview} data-testid="pdf-preview-cancel-btn"><X className="w-4 h-4 mr-2" />Cerrar</Button>
            <Button onClick={handleDownloadPdf} className="bg-green-600 hover:bg-green-700" data-testid="pdf-preview-download-btn"><Printer className="w-4 h-4 mr-2" />Descargar PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComprobantesOficina;
